import React, { useState } from "react";
import {
  BarChart3,
  TrendingUp,
  Download,
  Calendar,
  Layers,
  Filter,
  DollarSign,
  Boxes,
  Truck,
  Users
} from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { formatCurrency } from "../../utils/formatters";
import { exportToCSV } from "../../utils/exportHelper";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid
} from "recharts";

export function AnalyticsModule() {
  const { metrics, products, warehouses, suppliers, customers, salesOrders } = useApp();
  const [activeTab, setActiveTab] = useState("sales");

  const tabs = [
    { id: "sales", label: "Sales & Revenue" },
    { id: "purchase", label: "Purchase Velocity" },
    { id: "stock", label: "Stock Availability" },
    { id: "fastslow", label: "Fast vs Slow Moving" },
    { id: "deadstock", label: "Dead Stock Analysis" },
    { id: "expiry", label: "Expiry Horizon" },
    { id: "profit", label: "Profit & Margin" },
    { id: "warehouse", label: "Warehouse Performance" },
    { id: "supplier", label: "Supplier Scorecard" },
    { id: "customer", label: "Customer Segments" }
  ];

  // Analytical series data
  const monthlyRevenue = [
    { month: "Apr", revenue: 420000, profit: 54000, purchases: 340000 },
    { month: "May", revenue: 490000, profit: 68000, purchases: 390000 },
    { month: "Jun", revenue: 530000, profit: 74000, purchases: 410000 },
    { month: "Jul", revenue: 610000, profit: 89000, purchases: 480000 },
    { month: "Aug (M-T-D)", revenue: metrics.totalSalesAmount + 240000, profit: metrics.totalProfit + 32000, purchases: 290000 }
  ];

  const categoryMarginData = [
    { category: "Edible Oils", margin: 14.5, revenue: 145000 },
    { category: "Grains & Rice", margin: 18.2, revenue: 128000 },
    { category: "Atta & Flours", margin: 16.0, revenue: 98000 },
    { category: "Spices & Dals", margin: 24.5, revenue: 84000 },
    { category: "Biscuits & Snacks", margin: 22.0, revenue: 76000 },
    { category: "Personal Care", margin: 21.5, revenue: 62000 }
  ];

  const warehouseOccupancyData = warehouses.map((w, idx) => ({
    name: w.name.split("(")[0],
    occupancy: [78, 62, 85][idx] || 70,
    throughput: [1420, 890, 610][idx] || 500
  }));

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-500" />
            Enterprise Analytics & Intelligence Engine
          </h2>
          <p className="text-xs text-slate-500">
            Multi-dimensional reporting across sales, procurement, margin analysis, and inventory velocity
          </p>
        </div>

        <button
          onClick={() => exportToCSV("kirana_executive_report", monthlyRevenue, [{ key: "month" }, { key: "revenue" }, { key: "profit" }, { key: "purchases" }])}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 text-xs font-semibold rounded-xl transition"
        >
          <Download className="w-4 h-4" />
          Export Executive Report (CSV)
        </button>
      </div>

      {/* 10 Analysis Tabs Bar */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 border-b border-slate-200 dark:border-slate-800 text-xs scrollbar-thin">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3.5 py-2 rounded-xl font-semibold whitespace-nowrap transition ${
              activeTab === tab.id
                ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                : "bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dynamic Content per Tab */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main Chart (8 cols) */}
        <div className="lg:col-span-8 rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white font-display">
              {tabs.find((t) => t.id === activeTab)?.label} Visual Graph
            </h3>
            <span className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded">
              Interactive Tooltips Enabled
            </span>
          </div>

          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              {activeTab === "sales" || activeTab === "profit" ? (
                <AreaChart data={monthlyRevenue}>
                  <defs>
                    <linearGradient id="anRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(val) => [`₹${val.toLocaleString("en-IN")}`, ""]}
                    contentStyle={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "none", color: "#fff", fontSize: "12px" }}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="revenue" name="Revenue (₹)" stroke="#10b981" fillOpacity={1} fill="url(#anRev)" strokeWidth={2} />
                  <Area type="monotone" dataKey="profit" name="Net Profit (₹)" stroke="#0284c7" fill="#0284c7" fillOpacity={0.2} strokeWidth={2} />
                </AreaChart>
              ) : activeTab === "warehouse" ? (
                <BarChart data={warehouseOccupancyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "none", color: "#fff" }} />
                  <Legend />
                  <Bar dataKey="occupancy" name="Occupancy %" fill="#10b981" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="throughput" name="Monthly Units In/Out" fill="#0284c7" radius={[6, 6, 0, 0]} />
                </BarChart>
              ) : (
                <BarChart data={categoryMarginData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                  <XAxis dataKey="category" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "none", color: "#fff" }} />
                  <Legend />
                  <Bar dataKey="margin" name="Gross Margin %" fill="#10b981" radius={[6, 6, 0, 0]} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Analytical Insights & Summary (4 cols) */}
        <div className="lg:col-span-4 rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-4 text-xs">
          <div className="border-b border-slate-100 dark:border-slate-800 pb-3">
            <h4 className="text-sm font-bold text-slate-900 dark:text-white font-display">
              Executive AI Insights & KPIs
            </h4>
            <p className="text-[11px] text-slate-400 mt-0.5">Automated Kirana inventory intelligence</p>
          </div>

          <div className="space-y-3">
            <div className="p-3 bg-emerald-50/50 dark:bg-emerald-950/20 rounded-2xl border border-emerald-200/60 dark:border-emerald-900/40 space-y-1">
              <span className="font-bold text-emerald-800 dark:text-emerald-300">Top Revenue Driver:</span>
              <p className="text-slate-700 dark:text-slate-300 font-medium">
                Edible Oils & Basmati Rice account for 48% of total gross turnover.
              </p>
            </div>

            <div className="p-3 bg-sky-50/50 dark:bg-sky-950/20 rounded-2xl border border-sky-200/60 dark:border-sky-900/40 space-y-1">
              <span className="font-bold text-sky-800 dark:text-sky-300">Warehouse Utilization:</span>
              <p className="text-slate-700 dark:text-slate-300 font-medium">
                Bhiwandi Central Hub operating at 78% capacity; Dadar Express at 85%.
              </p>
            </div>

            <div className="p-3 bg-amber-50/50 dark:bg-amber-950/20 rounded-2xl border border-amber-200/60 dark:border-amber-900/40 space-y-1">
              <span className="font-bold text-amber-800 dark:text-amber-300">Capital Optimization:</span>
              <p className="text-slate-700 dark:text-slate-300 font-medium">
                Unlocking {formatCurrency(metrics.deadStockValue)} from slow-moving SKUs will boost net cash reserves by 14%.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
